(() => {
  if (window.PipWellsSecondRoomCartHandlerLoaded) return;
  window.PipWellsSecondRoomCartHandlerLoaded = true;

  const getRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const addCalendarDays = (date, days) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    result.setDate(result.getDate() + Number(days || 0));
    return result;
  };

  const europeanDeliveryCountries = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
  ]);

  const deliveryDaysForCountry = (country) => {
    if (country === 'NL') return { min: 1, max: 1 };
    if (country === 'BE') return { min: 1, max: 2 };
    if (country === 'DE' || country === 'FR') return { min: 2, max: 3 };
    if (country === 'GB') return { min: 3, max: 5 };
    if (europeanDeliveryCountries.has(country)) return { min: 2, max: 4 };
    return null;
  };

  const isBeforeCutoff = (date = new Date()) => {
    const hour = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Amsterdam',
      hour: '2-digit',
      hourCycle: 'h23'
    }).format(date);
    return Number(hour) < 23;
  };

  const deliveryDates = (startDate, endDate, locale) => {
    const date = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
    const start = date.format(startDate);
    const end = date.format(endDate);
    return start === end ? start : `${start} – ${end}`;
  };

  let detectedCountryPromise = null;
  const detectVisitorCountry = () => {
    if (detectedCountryPromise) return detectedCountryPromise;

    const endpoint = new URL(getRoot() + 'browsing_context_suggestions.json', window.location.origin);
    endpoint.searchParams.set('country[enabled]', 'true');

    detectedCountryPromise = fetch(endpoint.toString(), { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => payload && payload.detected_values && payload.detected_values.country
        ? payload.detected_values.country.handle
        : null)
      .catch(() => null);

    return detectedCountryPromise;
  };

  const updateDeliveryEstimates = async () => {
    const estimates = Array.from(document.querySelectorAll('[data-pw-delivery-estimate]'));

    for (const estimate of estimates) {
      const output = estimate.querySelector('[data-pw-delivery-text]');
      const note = estimate.querySelector('[data-pw-delivery-note]');
      if (!output) continue;

      const locale = estimate.dataset.locale || document.documentElement.lang || 'en';
      let country = String(estimate.dataset.country || '').toUpperCase();
      const now = new Date();

      // The international .com market has an ambiguous AT default. Keep the block hidden until
      // Shopify resolves the visitor country, then show only a range supported by the
      // current Shipping & Delivery policy.
      if (estimate.dataset.detectCountry === 'true') {
        country = String(await detectVisitorCountry() || '').toUpperCase();
        if (!deliveryDaysForCountry(country)) {
          estimate.dataset.resolvedCountry = '';
          estimate.hidden = true;
          continue;
        }
      }

      const deliveryDays = deliveryDaysForCountry(country);
      estimate.dataset.resolvedCountry = country;
      estimate.hidden = !deliveryDays;
      if (!deliveryDays) continue;

      const minDays = deliveryDays.min;
      const maxDays = deliveryDays.max;
      const cutoffPassed = !isBeforeCutoff(now);
      const dispatchDelay = cutoffPassed ? 1 : 0;
      const startDate = addCalendarDays(now, minDays + dispatchDelay);
      const endDate = addCalendarDays(now, maxDays + dispatchDelay);
      const dates = deliveryDates(startDate, endDate, locale);
      output.textContent = estimate.dataset.headingTemplate.replace('__DATES__', dates);

      if (note) {
        if (country === 'NL') {
          note.textContent = cutoffPassed
            ? estimate.dataset.nlAfterCutoffNote
            : estimate.dataset.nlCutoffNote;
        } else {
          note.textContent = cutoffPassed ? estimate.dataset.afterCutoffNote : estimate.dataset.beforeCutoffNote;
        }
      }
    }
  };

  const scheduleDeliveryUpdate = () => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(updateDeliveryEstimates));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateDeliveryEstimates, { once: true });
  } else {
    updateDeliveryEstimates();
  }

  document.addEventListener('shopify:section:load', scheduleDeliveryUpdate);
  document.addEventListener('cart:refresh', scheduleDeliveryUpdate);

  const deliveryObserver = new MutationObserver((records) => {
    const estimateWasAdded = records.some((record) => Array.from(record.addedNodes).some((node) =>
      node.nodeType === 1 && (
        node.matches('[data-pw-delivery-estimate]') ||
        node.querySelector('[data-pw-delivery-estimate]')
      )
    ));
    if (estimateWasAdded) scheduleDeliveryUpdate();
  });
  deliveryObserver.observe(document.documentElement, { childList: true, subtree: true });

  let protectionCorrectionPending = false;

  const refreshAfterProtectionUpdate = () => {
    if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  };

  const changeCartLine = async (id, quantity) => {
    const response = await fetch(getRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity })
    });
    if (!response.ok) throw new Error('Unable to correct shipping protection');
  };

  const validateProtectionCart = async (cart) => {
    if (!cart || !Array.isArray(cart.items) || protectionCorrectionPending) return false;

    const protectionVariantId = Number(
      (document.querySelector('[data-pw-shipping-protection]') || {}).dataset?.variantId || 0
    );
    const isProtectionLine = (item) =>
      item.product_handle === 'shipping-protection' ||
      item.handle === 'shipping-protection' ||
      (protectionVariantId > 0 && Number(item.variant_id) === protectionVariantId);
    const protectionLine = cart.items.find(isProtectionLine);
    if (!protectionLine) return false;

    const regularItemCount = cart.items
      .filter((item) => !isProtectionLine(item))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
    const desiredQuantity = regularItemCount === 0 ? 0 : 1;
    if (Number(protectionLine.quantity) === desiredQuantity) return false;

    protectionCorrectionPending = true;
    try {
      await changeCartLine(protectionLine.key || protectionLine.variant_id, desiredQuantity);
      return true;
    } finally {
      protectionCorrectionPending = false;
    }
  };

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart) return;
    validateProtectionCart(cart)
      .then((corrected) => {
        if (corrected) refreshAfterProtectionUpdate();
      })
      .catch(() => {});
  });

  fetch(getRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
    .then((response) => response.ok ? response.json() : null)
    .then((cart) => validateProtectionCart(cart))
    .then((corrected) => {
      if (corrected) refreshAfterProtectionUpdate();
    })
    .catch(() => {});

  document.addEventListener('change', async (event) => {
    const checkbox = event.target.closest('.pw-shipping-protection__checkbox');
    if (!checkbox || checkbox.disabled) return;

    const container = checkbox.closest('[data-pw-shipping-protection]');
    if (!container) return;

    const variantId = Number(container.dataset.variantId);
    const lineKey = container.dataset.lineKey;
    const adding = checkbox.checked;
    const status = container.querySelector('.pw-shipping-protection__status');
    let mutationSucceeded = false;

    container.setAttribute('aria-busy', 'true');
    checkbox.disabled = true;
    if (status) status.hidden = true;

    try {
      const endpoint = adding ? 'cart/add.js' : 'cart/change.js';
      const body = adding
        ? { items: [{ id: variantId, quantity: 1, properties: { _pw_shipping_protection: 'true' } }] }
        : { id: lineKey || variantId, quantity: 0 };
      const response = await fetch(getRoot() + endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('Unable to update shipping protection');
      mutationSucceeded = true;

      let cart = null;
      try {
        const cartResponse = await fetch(getRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (error) {
        cart = null;
      }
      if (cart) await validateProtectionCart(cart);
      refreshAfterProtectionUpdate();
    } catch (error) {
      if (mutationSucceeded) {
        refreshAfterProtectionUpdate();
        return;
      }
      checkbox.checked = !adding;
      checkbox.disabled = false;
      container.removeAttribute('aria-busy');
      if (status) status.hidden = false;
    }
  });

  const eligibleGiftSetHandles = new Set([
    'pure-one-diffuser-essence-of-luxury-giftbox-3-120ml',
    'pure-one-diffuser-silver-essence-of-luxury-giftbox-3-120ml'
  ]);

  const qualifyingGiftSetQuantity = (cart) => (cart.items || []).reduce((total, item) => {
    const handle = item.handle || item.product_handle;
    return eligibleGiftSetHandles.has(handle) ? total + Number(item.quantity || 0) : total;
  }, 0);

  const secondRoomDiscountIsPresent = (cart) => {
    const matchesSecondRoom = (value) => String(value || '').toUpperCase() === 'SECONDROOM10';
    const lineDiscountIsPresent = (cart.items || []).some((item) =>
      (item.discount_allocations || []).some((allocation) => {
        const title = allocation.discount_application && allocation.discount_application.title;
        return matchesSecondRoom(title);
      })
    );
    const cartDiscountIsPresent = (cart.cart_level_discount_applications || []).some((application) =>
      matchesSecondRoom(application.title)
    );
    const discountCodeIsPresent = (cart.discount_codes || []).some((discount) =>
      matchesSecondRoom(discount.code) && discount.applicable !== false
    );

    return lineDiscountIsPresent || cartDiscountIsPresent || discountCodeIsPresent;
  };

  const checkoutPreparationMessage = () => {
    const language = String(document.documentElement.lang || 'en').toLowerCase();
    if (language.startsWith('nl')) return 'Afrekenen wordt voorbereid.';
    if (language.startsWith('de')) return 'Der Checkout wird vorbereitet.';
    return 'Preparing checkout.';
  };

  const checkoutDiscountMessage = () => {
    const language = String(document.documentElement.lang || 'en').toLowerCase();
    if (language.startsWith('nl')) return 'Korting wordt toegepast. Je gaat nu naar afrekenen.';
    if (language.startsWith('de')) return 'Rabatt wird angewendet. Sie werden jetzt zur Kasse weitergeleitet.';
    return 'Applying discount. Continuing to checkout.';
  };

  const checkoutErrorMessage = () => {
    const language = String(document.documentElement.lang || 'en').toLowerCase();
    if (language.startsWith('nl')) return 'Afrekenen kon niet worden voorbereid. Probeer het opnieuw.';
    if (language.startsWith('de')) return 'Der Checkout konnte nicht vorbereitet werden. Bitte versuchen Sie es erneut.';
    return 'Checkout could not be prepared. Please try again.';
  };

  const ensureCheckoutStatus = (form) => {
    let status = form.querySelector('[data-pw-checkout-status]');
    if (status) return status;

    status = document.createElement('span');
    status.className = 'sr-only';
    status.dataset.pwCheckoutStatus = 'true';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    form.appendChild(status);
    return status;
  };

  const persistCartAttributes = async (form, signal) => {
    const attributes = {};
    form.querySelectorAll('[name^="attributes["]').forEach((field) => {
      if (field.disabled || !field.name.endsWith(']')) return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;

      const key = field.name.slice('attributes['.length, -1);
      if (key) attributes[key] = field.value;
    });

    if (Object.keys(attributes).length === 0) return true;

    const response = await fetch(getRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
      signal
    });
    return response.ok;
  };

  let checkoutNavigationStarted = false;

  const resumeNativeCheckout = (form, submitter, status) => {
    checkoutNavigationStarted = false;
    if (status) status.textContent = '';
    if (submitter) {
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
    }
    form.dataset.pwSecondRoomCheckoutBypass = 'true';
    form.requestSubmit(submitter || form.querySelector('[name="checkout"]'));
  };

  const stopCheckoutWithError = (submitter, status) => {
    checkoutNavigationStarted = false;
    if (submitter) {
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
    }
    if (status) {
      status.className = 'pw-checkout-status text-xs text-subdued';
      status.textContent = checkoutErrorMessage();
    }
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form.pw-cart-checkout-form');
    if (!form) return;

    if (form.dataset.pwSecondRoomCheckoutBypass === 'true') {
      delete form.dataset.pwSecondRoomCheckoutBypass;
      return;
    }

    const submitter = event.submitter || form.querySelector('[name="checkout"]');
    if (!submitter || submitter.name !== 'checkout') return;

    const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!shopifyRoot) return;
    const verifiedRoot = shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;

    event.preventDefault();
    if (checkoutNavigationStarted) return;

    checkoutNavigationStarted = true;
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
    const status = ensureCheckoutStatus(form);
    status.className = 'sr-only';
    status.textContent = checkoutPreparationMessage();

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 3000);

    try {
      const root = verifiedRoot;
      const cartResponse = await fetch(root + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: controller.signal
      });
      if (!cartResponse.ok) throw new Error('Unable to verify cart before checkout');

      const cart = await cartResponse.json();
      const discountRequired = qualifyingGiftSetQuantity(cart) >= 2 && !secondRoomDiscountIsPresent(cart);

      if (!discountRequired) {
        window.clearTimeout(timeout);
        resumeNativeCheckout(form, submitter, status);
        return;
      }

      status.textContent = checkoutDiscountMessage();

      const attributesSaved = await persistCartAttributes(form, controller.signal);
      if (!attributesSaved) throw new Error('Unable to preserve cart attributes before checkout');

      window.clearTimeout(timeout);
      const checkoutUrl = root + 'checkout';
      window.location.assign(
        root + 'discount/SECONDROOM10?redirect=' + encodeURIComponent(checkoutUrl)
      );
    } catch (error) {
      window.clearTimeout(timeout);
      stopCheckoutWithError(submitter, status);
    }
  }, true);

  document.documentElement.addEventListener('cart:change', () => {
    scheduleDeliveryUpdate();
  });

  const setMessage = (button, message) => {
    const offer = button.closest('.pw-second-room-offer, .pw-cart-related');
    const status = offer && offer.querySelector('[data-pw-second-room-status], [data-pw-cart-smart-status]');
    if (status) status.textContent = message;
  };

  /*
    SECONDROOM10 used to be applied here during cart:change with a redirect to /cart.
    It is now intentionally deferred to checkout intent so product adds remain in the drawer.
  */

  // Matches the theme's own extractSectionId(): the closest ".shopify-section" ancestor's id,
  // with the "shopify-section-" prefix stripped.
  const closestSectionId = (element) => {
    const container = element.closest('.shopify-section');
    return container ? container.id.replace('shopify-section-', '') : null;
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-pw-second-room-add], [data-pw-cart-smart-add]');
    if (!button || button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setMessage(button, '');

    let addSucceeded = false;

    try {
      const root = getRoot();

      // This snippet is rendered on two different surfaces: the cart drawer (always present in
      // the DOM via the overlay-group, on every page) and the full /cart page's own line-item
      // list (sections/main-cart.liquid). The drawer already refreshes itself in place via the
      // theme's native cart:change listener. The cart page has no such listener for its own
      // section, so -- to avoid ever reloading or navigating away from the drawer experience --
      // we ask Shopify to re-render whichever section actually contains this button and swap it
      // in place ourselves, the same way the theme does it internally.
      const inDrawer = !!button.closest('cart-drawer');
      const drawerSectionId = inDrawer ? closestSectionId(button) : null;
      const ownSectionId = inDrawer ? null : closestSectionId(button);

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));
      if (ownSectionId && !sectionsToBundle.includes(ownSectionId)) {
        sectionsToBundle.push(ownSectionId);
      }

      // cart/add.js merges automatically when the same variant is already in the cart
      // (matte black -> quantity +1) and adds a new line for a different variant
      // (matte silver -> separate Gift Set line). No pre-fetch or change.js needed.
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: sectionsToBundle,
          sections_url: window.location.pathname + window.location.search
        })
      });

      if (!response.ok) throw new Error('Unable to update cart');
      const addResult = await response.json();
      addSucceeded = true;

      // Read the authoritative cart for the freshly rendered drawer state. SECONDROOM10 is
      // deliberately deferred until checkout intent so adding a product never leaves the drawer.
      let cart = null;
      try {
        const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch (error) {
        cart = null;
      }
      // On the full /cart page, reload after a confirmed add. This guarantees that the line,
      // subtotal, discount and next applicable upsell are visible even if Shopify returns
      // stale or incomplete section HTML. The drawer keeps its faster in-place update below.
      if (!inDrawer) {
        window.location.reload();
        return;
      }

      // Attach the freshly rendered section HTML, then hand it to the theme's native
      // cart:change handler so the drawer and cart-count badge update in place.

      const renderedSections = addResult && addResult.sections;
      const requiredSectionId = inDrawer ? drawerSectionId : ownSectionId;
      const requiredMarkup = requiredSectionId && renderedSections && renderedSections[requiredSectionId];
      const hasRequiredSection = typeof requiredMarkup === 'string' && requiredMarkup.trim() !== '';

      if (cart && hasRequiredSection) {
        cart.sections = addResult.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        }));

        // Surfaces with no native cart:change listener of their own (e.g. the /cart page's own
        // line-item table) get swapped in place here: replace the whole rendered section with
        // the fresh server-rendered markup, exactly like the drawer does for itself internally.
        if (ownSectionId) {
          const freshMarkup = addResult.sections[ownSectionId];
          const freshDoc = freshMarkup ? new DOMParser().parseFromString(freshMarkup, 'text/html') : null;
          const freshSection = freshDoc && freshDoc.querySelector('.shopify-section');
          const currentSection = document.getElementById(`shopify-section-${ownSectionId}`);

          if (freshSection && currentSection) {
            currentSection.replaceWith(freshSection);
          } else {
            // A cart page must never look unchanged after a successful upsell. Shopify can
            // occasionally omit a requested section from an async response, so reload only
            // in that exceptional case; normally the section is updated in place.
            window.location.reload();
            return;
          }
        }
      } else {
        // Safety net (should not happen): no rendered sections came back, so ask the theme
        // to re-fetch and re-render the drawer itself.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      if (addSucceeded) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
      setMessage(button, button.getAttribute('data-pw-error-message') || 'Please try again.');
    }
  });
})();
